/* eslint-disable react/jsx-boolean-value */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ConfirmationModal } from '@components';
import DropDownActionButton from '@components/dropdown-action-button/DropDownActionButton';
import SegmentedControl from '@components/segmented-control/SegmentedControl';
import { FEATURE_FLAGS } from '@constants/feature';
import UserPermissionTypes from '@constants/permissions';
import { LEAD_MANAGEMENT } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  sendMixpanelEvent,
  trackPageView
} from '@core/analytics/mixpanel';
import { api } from '@core/baseApi';
import { useAppDispatch, useAppSelector } from '@core/store';
import BulkAssignBottomBar from '@features/lead-management/components/BulkAssignBottomBar';
import DeleteConfirmationModal from '@features/lead-management/components/lead-activity-panel/DeleteConfirmationModal';
import LeadsAddTagModal from '@features/lead-management/components/LeadsAddTagModal';
import useMetaProvider from '@features/lead-management/hooks/useMetaProvider';
import useMetaSyncPolling from '@features/lead-management/hooks/useMetaSyncPolling';
import { addMetaSync } from '@features/lead-management/metaSyncSlice';
import {
  useDeleteLeadMutation,
  useLazyGetAgentsQuery,
  useLazyGetLeadsQuery,
  useLazyGetTagsQuery,
  useLeadBulkAssignMutation,
  useLeadBulkUpdateMutation,
  useUpdateLeadAssigneeMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import { setLeadListPage } from '@features/lead-management/slices/leadSlice';
import { BulkAssignee, LeadType } from '@features/lead-management/types/common';
import { buildListFilteredMixpanelPayload } from '@features/lead-management/utils/leadAnalytics';
import getSaveLeadErrorMessage, {
  getDeleteLeadErrorMessage
} from '@features/lead-management/utils/leadApiErrorMessages';
import {
  getMetaProviderErrorMessage,
  META_SYNC_STATUS
} from '@features/lead-management/utils/metaIntegrationHelpers';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';
import { Agent, Tag } from 'types/lead-management';

import LeadBulkUploadModal from './LeadBulkUploadModal';
import LeadListEmptyView from './LeadListEmptyView';
import LeadTable from './LeadTable';

import LeadAnalytics from '../../components/analytics/LeadAnalytics';
import SetupMetaIntegrationModal from '../../components/lead-settings/SetupMetaIntegrationModal';
import LeadFilters from '../../components/LeadFilter';
import { UNASSIGNED_ASSIGNEE } from '../../constants/common';
import { LEAD_VIEW_OPTIONS, LeadView } from '../../constants/leadAnalytics';
import { getAddLeadOptionsForOrg } from '../../constants/leadList';
import useLeadAgentRestrictions from '../../hooks/useLeadAgentRestrictions';
import useLeadListFilters from '../../hooks/useLeadListFilters';

const LeadList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const page = useAppSelector((state) => state.lead.currentPage);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );
  const isMetaLeadIngestionEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.META_LEAD_INGESTION
  );

  const {
    isAssigneeSelectionDisabled,
    isLeadDeletionDisabled,
    isBulkAssignEnabled,
    isLoading: isRestrictionConfigLoading
  } = useLeadAgentRestrictions();

  const { checkPermission } = useCheckPermission();
  const canCreateLead = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CREATE
  ]);

  const setPage = (newPage: number) => {
    dispatch(setLeadListPage(newPage));
  };

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [bulkAssigneeId, setBulkAssigneeId] = useState<string>('');
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingLeads, setIsFetchingLeads] = useState(true);
  const [didInitialCheck, setDidInitialCheck] = useState(false);
  const [showEmptyFromInitial, setShowEmptyFromInitial] = useState(false);
  const [isMetaSetupModalOpen, setIsMetaSetupModalOpen] = useState(false);
  const [leadView, setLeadView] = useState(LeadView.LIST);

  const { createSuccessAlert, createErrorAlert } = useNotify();

  const [getLeads, { data: leadsList, isLoading: isLeadsLoading }] =
    useLazyGetLeadsQuery();
  const [getTags, { data: tagsList }] = useLazyGetTagsQuery();
  const [getAgents, { data: agentsList }] = useLazyGetAgentsQuery();
  const [leadBulkUpdate] = useLeadBulkUpdateMutation();
  const [leadBulkAssign, { isLoading: isBulkAssigning }] =
    useLeadBulkAssignMutation();
  const [updateLeadAssignee] = useUpdateLeadAssigneeMutation();
  const [deleteLead] = useDeleteLeadMutation();
  const isFirstFetch = useRef(true);

  const {
    providerData,
    createProvider: createMetaProvider,
    updateProvider: updateMetaProvider
  } = useMetaProvider(organizationId, { skip: !isMetaLeadIngestionEnabled });
  const { pollMetaSyncStatus } = useMetaSyncPolling();

  const {
    search,
    selectedSources,
    selectedStatusIds,
    selectedTagIds,
    selectedAdIds,
    hasActiveFilters,
    toQueryString,
    setSearch,
    setSelectedSources,
    setSelectedStatusIds,
    setSelectedTagIds,
    setSelectedAdIds,
    adsSelectMode,
    setAdsSelectMode,
    excludedAdIds,
    setExcludedAdIds,
    sourceOptions,
    statusOptions,
    tagOptions,
    adOptions,
    selectedAssigneeIds,
    setSelectedAssigneeIds,
    assigneeOptions,
    setDateRange,
    selectedDateRange,
    setDateRangeType,
    dateRangeType,
    stuckDaysFilter,
    setStuckDaysFilter,
    sortState,
    setSort,
    fetchAds,
    loadMoreAds,
    adsHasMore,
    adsIsLoading,
    adsTotalCount
  } = useLeadListFilters();

  const handleFetchingLeads = async (isInitialFetch: boolean = false) => {
    setIsFetchingLeads(true);
    try {
      const pageToFetch = page ?? 0;
      const queryString = toQueryString({ page: pageToFetch });
      const response = await getLeads({ organizationId, queryString });
      if (response.data) {
        setPage(response.data.pagination?.page);
        if (isInitialFetch) {
          setDidInitialCheck(true);
          setShowEmptyFromInitial(
            !(response.data.data?.length > 0) && !hasActiveFilters
          );
        }
      } else {
        setPage(pageToFetch);
      }
    } catch (error) {
      createErrorAlert('Failed to fetch leads. Please try again.');
    }
    setIsFetchingLeads(false);
  };

  const getDeleteModalMessage = () => {
    if (!selectedLeadId) return '';
    const selectedLead = leadsList?.data?.find(
      (lead: LeadType) => lead.id === selectedLeadId
    );
    return `Are you sure you want to delete ${selectedLead?.name}?`;
  };

  const handleDeleteLead = async (leadId: string) => {
    setSelectedLeadId(leadId);
    setIsDeleteModalOpen(true);
  };

  const onConfirmDeleteLead = async () => {
    try {
      await deleteLead({ organizationId, leadId: selectedLeadId }).unwrap();
      createSuccessAlert('Lead deleted successfully.');
      setIsDeleteModalOpen(false);
      setSelectedLeadId('');

      const currentData = leadsList?.data ?? [];
      const isLastItemOnPage = currentData.length === 1;
      const newPage = isLastItemOnPage && page > 0 ? page - 1 : page;

      setPage(newPage);
      getLeads({
        organizationId,
        queryString: toQueryString({ page: newPage })
      });
    } catch (error) {
      createErrorAlert(getDeleteLeadErrorMessage(error));
    }
  };

  const handleMetaSyncComplete = async (status: string) => {
    if (
      status === META_SYNC_STATUS.COMPLETED ||
      status === META_SYNC_STATUS.PARTIAL_SUCCESS
    ) {
      dispatch(api.util.invalidateTags(['LeadManagement']));

      const pageToFetch = page || 0;
      const queryString = toQueryString({ page: pageToFetch });
      const leadsResponse = await getLeads({
        organizationId,
        queryString
      });

      if (leadsResponse.data?.data?.length > 0) {
        setShowEmptyFromInitial(false);
      }
    }
  };

  const handleSyncFromMeta = async () => {
    if (!providerData) {
      setIsMetaSetupModalOpen(true);
      return;
    }

    try {
      const response = await updateMetaProvider({
        organizationId,
        providerId: providerData?.id ?? '',
        body: { is_active: true }
      }).unwrap();

      if (response?.job_id) {
        const syncId = `meta-sync-${Date.now()}`;
        dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

        await pollMetaSyncStatus({
          syncId,
          jobId: response.job_id,
          onComplete: handleMetaSyncComplete
        });
      }
    } catch (error) {
      createErrorAlert(getMetaProviderErrorMessage(error), {
        classOverride: 'max-w-xl'
      });
    }
  };

  const handleAddOption = (option: string) => {
    if (option === 'add-manually') {
      navigate(LEAD_MANAGEMENT.LEAD_ADD);
    } else if (option === 'import-csv') {
      setIsCsvUploadModalOpen(true);
    } else if (option === 'sync-from-meta') {
      handleSyncFromMeta();
    }
  };

  const handleCsvUploadSuccess = (isInitialFetch: boolean = false) => {
    setIsCsvUploadModalOpen(false);
    handleFetchingLeads();
    if (isInitialFetch) {
      setShowEmptyFromInitial(false);
    }
  };

  const handleCsvModalClose = () => {
    setIsCsvUploadModalOpen(false);
  };

  const handleMetaSetupConfirm = async (accessToken: string, date: Date) => {
    try {
      const response = await createMetaProvider({
        organizationId,
        body: {
          access_token: accessToken,
          is_active: true,
          initial_sync_date: date.toISOString()
        }
      }).unwrap();

      setIsMetaSetupModalOpen(false);

      if (response?.job_id) {
        const syncId = `meta-sync-${Date.now()}`;
        dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

        await pollMetaSyncStatus({
          syncId,
          jobId: response.job_id,
          onComplete: handleMetaSyncComplete
        });
      } else {
        createErrorAlert('Meta integration failed. Please try again.');
      }
    } catch (error) {
      createErrorAlert(getMetaProviderErrorMessage(error), {
        classOverride: 'max-w-xl'
      });
    }
  };

  const handleAddTagButtonClick = (leadId: string) => {
    setSelectedLeadId(leadId);
    setIsTagModalOpen(true);
  };

  const bulkAssigneeOptions: BulkAssignee[] = [
    { id: UNASSIGNED_ASSIGNEE.id, name: UNASSIGNED_ASSIGNEE.name },
    ...(agentsList?.sales_agents ?? []).map((agent: Agent) => ({
      id: agent.user_id,
      name: agent.name
    }))
  ];

  const isBulkUnassign = bulkAssigneeId === UNASSIGNED_ASSIGNEE.id;

  const clearBulkSelection = () => {
    setSelectedRows([]);
    setBulkAssigneeId('');
  };

  const handleBulkAssignClick = () => {
    if (!bulkAssigneeId) return;
    setIsBulkAssignModalOpen(true);
  };

  const getBulkAssigneeName = () =>
    bulkAssigneeOptions.find((option) => option.id === bulkAssigneeId)?.name ??
    '';

  const getBulkAssignConfirmTitle = () => {
    const leadLabel = selectedRows.length > 1 ? 'leads' : 'lead';
    const action = isBulkUnassign
      ? 'unassigned'
      : `assigned to ${getBulkAssigneeName()}`;
    return `The selected ${selectedRows.length} ${leadLabel} will be ${action}.`;
  };

  const onConfirmBulkAssign = async () => {
    try {
      const assignedCount = selectedRows.length;
      await leadBulkAssign({
        organizationId,
        leadIds: selectedRows,
        assigneeId: isBulkUnassign ? null : bulkAssigneeId
      }).unwrap();

      sendMixpanelEvent(Event.LEAD_ASSIGNED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        assignee_id: isBulkUnassign ? null : bulkAssigneeId,
        assignment_method: 'bulk',
        lead_count: assignedCount
      });

      setIsBulkAssignModalOpen(false);
      clearBulkSelection();
      getLeads({ organizationId, queryString: toQueryString({ page }) });
      createSuccessAlert(
        `${assignedCount} ${assignedCount > 1 ? 'leads' : 'lead'} ${
          isBulkUnassign ? 'unassigned' : 'assigned'
        } successfully.`
      );
    } catch (err: any) {
      setIsBulkAssignModalOpen(false);
      createErrorAlert(
        getSaveLeadErrorMessage(err, false, { updateContext: 'assignee' })
      );
    }
  };

  const handleUpdateAssignee = async (
    leadId: string,
    assignee: { id: string; isUnassigned: boolean }
  ) => {
    if (!leadId || !assignee?.id) {
      createErrorAlert('Invalid assignee selection.');
      return;
    }
    try {
      await updateLeadAssignee({
        organizationId,
        leadId,
        assignee: assignee.isUnassigned ? null : assignee.id
      }).unwrap();

      // Mixpanel event for lead assigned
      const previousAssignee = leadsList?.data?.find(
        (lead: LeadType) => lead.id === leadId
      )?.assignee?.id;

      sendMixpanelEvent(Event.LEAD_ASSIGNED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        assignee_id: assignee.isUnassigned ? null : assignee.id,
        assignment_method: 'manual',
        previous_assignee: previousAssignee || null,
        is_reassignment: !!previousAssignee
      });
      getLeads({ organizationId, queryString: toQueryString({ page }) });
      createSuccessAlert('Assignee updated successfully.');
    } catch (err: any) {
      createErrorAlert(
        getSaveLeadErrorMessage(err, false, { updateContext: 'assignee' })
      );
    }
  };

  const handlePagination = (pageNumber: number) => {
    setIsFetchingLeads(true);
    try {
      const queryString = toQueryString({ page: pageNumber });
      getLeads({ organizationId, queryString }).then((response) => {
        if (response.data) {
          setPage(response.data.pagination?.page);
        }
      });
    } catch (error: any) {
      createErrorAlert(error?.data?.message || 'Failed to fetch leads.');
    }
    setIsFetchingLeads(false);
  };

  const handleFetchingTags = async (searchInput: string) => {
    await getTags({ organizationId, searchTerm: searchInput });
  };

  const handleFetchingAgents = async () => {
    await getAgents({ organizationId });
  };

  const getExistingTagsOfSelectedLead = () => {
    const selectedLead = leadsList?.data?.find(
      (lead: LeadType) => lead.id === selectedLeadId
    );
    return selectedLead?.tags;
  };

  const handleAddTags = (tags: Tag[]) => {
    try {
      leadBulkUpdate({
        leadIds: selectedRows.length ? selectedRows : [selectedLeadId],
        tagIds: tags.map((tag) => tag.id)
      }).then(() => {
        setIsTagModalOpen(false);
        createSuccessAlert('Tags added to lead successfully');
      });
    } catch (error) {
      createErrorAlert('Failed to add tags to lead. Please try again.');
    }
  };

  useEffect(() => {
    handleFetchingTags('');
    handleFetchingAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleFetchingLeads(isFirstFetch.current);
    isFirstFetch.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    selectedSources,
    selectedStatusIds,
    selectedTagIds,
    selectedAssigneeIds,
    selectedAdIds,
    adsSelectMode,
    excludedAdIds,
    selectedDateRange,
    dateRangeType,
    stuckDaysFilter,
    sortState
  ]);

  // Mixpanel event for leads list viewed
  const hasTrackedLeads = useRef(false);
  useEffect(() => {
    const hasData = leadsList?.data?.length;
    if (!hasData || hasTrackedLeads.current || location.state?.fromLeadCreation)
      return;
    hasTrackedLeads.current = true;

    trackPageView('Leads list page viewed');
    sendMixpanelEvent(Event.LEADS_LIST_VIEWED, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      total_leads_shown: leadsList.data.length,
      total_leads_count: leadsList.pagination?.total_rows ?? 0,
      page_number: (leadsList.pagination?.page ?? 0) + 1,
      filters_active: buildListFilteredMixpanelPayload({
        status: selectedStatusIds,
        source: selectedSources,
        tags: selectedTagIds,
        assignee: selectedAssigneeIds,
        ads: selectedAdIds,
        dateRangeType,
        dateRange: selectedDateRange
      })?.filter_value
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsList]);

  // If there are no leads and no filters, show the import view
  const hasNoLeads = (leadsList?.data?.length ?? 0) === 0;
  const totalLeadsCount = leadsList?.pagination?.total_rows;
  const showImportView =
    showEmptyFromInitial ||
    (!isFetchingLeads && !isLeadsLoading && !hasActiveFilters && hasNoLeads);

  if (didInitialCheck && showImportView && !isRestrictionConfigLoading) {
    return (
      <LeadListEmptyView
        organizationId={organizationId}
        onAddManually={() => navigate(LEAD_MANAGEMENT.LEAD_ADD)}
        showMetaOption={isMetaLeadIngestionEnabled}
        onSyncFromMeta={handleSyncFromMeta}
        onCsvUploadSuccess={() => handleCsvUploadSuccess(true)}
        onMetaSetupConfirm={handleMetaSetupConfirm}
        metaProvider={providerData}
        restrictLeadCreationForAgent={isAssigneeSelectionDisabled}
        canCreateLead={canCreateLead}
        isMetaSetupModalOpen={isMetaSetupModalOpen}
        onMetaSetupModalClose={() => setIsMetaSetupModalOpen(false)}
      />
    );
  }

  return (
    <div className="relative -mb-21 flex h-full flex-col">
      <Head title="Leads" />
      <div className="flex flex-col">
        <div className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-x-3">
            <div className="text-2xl font-bold text-content">Leads</div>
            {totalLeadsCount != null && (
              <span className="rounded-full bg-cofeeNeutral3 px-3 py-0.5 text-sm font-medium text-cofeeNeutral10">
                {totalLeadsCount}
              </span>
            )}
          </div>
          <SegmentedControl
            options={LEAD_VIEW_OPTIONS}
            selectedId={leadView}
            onChange={(option) => setLeadView(option.id as LeadView)}
            className="!bg-cofeeGreyBlue100"
          />
        </div>
      </div>
      {leadView === LeadView.ANALYTICS ? (
        <LeadAnalytics />
      ) : (
        <div className="flex-1 rounded-2xl py-6">
          <LeadFilters
            search={search}
            setSearch={setSearch}
            sourceOptions={sourceOptions}
            statusOptions={statusOptions}
            tagOptions={tagOptions}
            adOptions={adOptions}
            selectedSources={selectedSources}
            selectedStatusIds={selectedStatusIds}
            selectedTagIds={selectedTagIds}
            selectedAdIds={selectedAdIds}
            setSelectedSources={setSelectedSources}
            setSelectedStatusIds={setSelectedStatusIds}
            setSelectedTagIds={setSelectedTagIds}
            setSelectedAdIds={setSelectedAdIds}
            adsSelectMode={adsSelectMode}
            setAdsSelectMode={setAdsSelectMode}
            excludedAdIds={excludedAdIds}
            setExcludedAdIds={setExcludedAdIds}
            selectedAssigneeIds={selectedAssigneeIds}
            setSelectedAssigneeIds={setSelectedAssigneeIds}
            assigneeOptions={assigneeOptions}
            setDateRange={setDateRange}
            setDateRangeType={setDateRangeType}
            dateRangeType={dateRangeType}
            stuckDaysFilter={stuckDaysFilter}
            setStuckDaysFilter={setStuckDaysFilter}
            resultsCount={leadsList?.pagination?.total_rows}
            fetchAds={fetchAds}
            loadMoreAds={loadMoreAds}
            adsHasMore={adsHasMore}
            adsIsLoading={adsIsLoading}
            adsTotalCount={adsTotalCount}
          >
            {canCreateLead && (
              <DropDownActionButton
                label="Add Lead"
                dropdownOptions={getAddLeadOptionsForOrg(
                  orgFeatureFlags,
                  providerData,
                  isAssigneeSelectionDisabled
                )}
                leftIconName="add-icon"
                onDropdownSelect={(option) => handleAddOption(option.value)}
              />
            )}
          </LeadFilters>
          <LeadTable
            handleMenuIconClick={() => {}}
            leadDetails={leadsList}
            page={page}
            setPage={handlePagination}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onUpdateAssignee={handleUpdateAssignee}
            handleAddTagButtonClick={handleAddTagButtonClick}
            agentsList={agentsList?.sales_agents || []}
            handleDeleteLead={handleDeleteLead}
            isListLoading={isLeadsLoading || isFetchingLeads}
            isAssigneeDisabled={isAssigneeSelectionDisabled}
            canMultiSelect={isBulkAssignEnabled}
            hideDeleteAction={isLeadDeletionDisabled}
            sortState={sortState}
            onSortChange={setSort}
          />
          {selectedRows.length > 0 &&
            isBulkAssignEnabled &&
            !isBulkAssignModalOpen && (
              <BulkAssignBottomBar
                selectedCount={selectedRows.length}
                assigneeOptions={bulkAssigneeOptions}
                selectedAssigneeId={bulkAssigneeId}
                onSelectAssignee={setBulkAssigneeId}
                onClearSelection={clearBulkSelection}
                onAssign={handleBulkAssignClick}
                isAssigning={isBulkAssigning}
              />
            )}
        </div>
      )}
      {isTagModalOpen && (
        <LeadsAddTagModal
          isOpen={isTagModalOpen}
          onClose={() => {
            setIsTagModalOpen(false);
          }}
          onAddTags={handleAddTags}
          existingTags={getExistingTagsOfSelectedLead()}
          tagsList={tagsList}
          isBulkEdit={selectedRows.length > 1}
          existingSelectedTags={[]}
        />
      )}
      {isCsvUploadModalOpen && (
        <LeadBulkUploadModal
          isOpen={isCsvUploadModalOpen}
          handleCancel={handleCsvModalClose}
          onCreateSuccess={handleCsvUploadSuccess}
          organizationId={organizationId}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          showDeleteModal={isDeleteModalOpen}
          handleCancel={() => setIsDeleteModalOpen(false)}
          handleConfirmDelete={onConfirmDeleteLead}
          title="Delete Lead"
          message={getDeleteModalMessage()}
        />
      )}
      {isMetaSetupModalOpen && (
        <SetupMetaIntegrationModal
          isOpen={isMetaSetupModalOpen}
          onClose={() => setIsMetaSetupModalOpen(false)}
          onConfirm={handleMetaSetupConfirm}
        />
      )}
      {isBulkAssignModalOpen && (
        <ConfirmationModal
          isOpen={isBulkAssignModalOpen}
          iconName="bulk-assign-icon"
          iconSize="56"
          titleStyle="!font-semibold"
          wrapperClass="!bg-white"
          title={getBulkAssignConfirmTitle()}
          message="Are you sure you want to proceed?"
          messageStyle="!pb-4"
          cancelButtonLabel="Cancel"
          confirmButtonLabel="Proceed"
          handleCancel={() => setIsBulkAssignModalOpen(false)}
          handleConfirm={onConfirmBulkAssign}
          isConfirmButtonLoading={isBulkAssigning}
          isCancelButtonDisabled={isBulkAssigning}
          className="w-[367px] !p-[25px]"
          primaryButtonVariant="primary"
          primaryButtonSize="medium"
          secondaryButtonVariant="tertiary"
          secondaryButtonSize="medium"
        />
      )}
    </div>
  );
};

export default LeadList;
